import {
	Form,
	FormItem,
	IFramePermission,
	Select,
	SelectOption,
	ValidationError,
} from "client/Components";
import {
	EditTileFromData,
	initialGenericState
} from "client/Features/Workspace/LeftSliders/EditTile/EditGenericTile";
import { EditTileGeneralFields } from "client/Features/Workspace/LeftSliders/EditTile/GeneralFields";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	createValidationConfiguration,
	IValidationConfiguration,
} from "client/Validation";
import {
	getDashboards,
	getGroups,
	getTiles,
	TileModel,
} from "client/Api/Apps/PowerBi/powerBi";
import {
	forwardRef,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	TileEditMode,
} from "./common";
import {
	AddTilesResponseModel,
	PowerBiUpdateTileModel,
	PowerBiDetailsTileModel,
	PowerBiCreateTileModel,
	createPowerBiTile,
	updatePowerBiTile
} from "client/Api/Workspace/workspace";
import { Guid } from "client_legacy/ndaw/guid";
import styles from "./EditPowerBiTile.module.less";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "../../../../Validation/rules";
import { useAlerts } from "../../../../Hooks";
import { EditTileHandle } from "./EditTile";
import { useCreateTile } from "./hooks/useCreateTile";
import { useUpdateTile } from "./hooks/useUpdateTile";
import { PowerBiEmbed } from "../../Tiles/PowerBi/PowerBiEmbed";

type PowerBiTileFormData = EditTileFromData<PowerBiUpdateTileModel>;

type PowerBiGroups = readonly SelectOption<Guid | null>[];
type PowerBiDashboards = readonly SelectOption<Guid>[];
type PowerBiTiles = readonly SelectOption<Guid>[];

type EditPowerBiTileProps = {
	isLoading: boolean;
	onIsLoadingChange: (value: boolean) => void;
	initialModel: PowerBiDetailsTileModel;
	mode: TileEditMode;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

const validationBuilder: IValidationConfiguration<PowerBiTileFormData> =
	createValidationConfiguration<PowerBiTileFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationBuilder
	.forScalar("dashboardId")
	.add(isNotNullNorUndefined());

validationBuilder
	.forScalar("powerBiTileId")
	.add(isNotNullNorUndefined());

export const EditPowerBiTile = forwardRef(
	(props: EditPowerBiTileProps, ref: Ref<EditTileHandle>) => {
		const {
			initialModel,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave,
		} = props;

		const [groups, setGroups] = useState<PowerBiGroups>([]);
		const [dashboards, setDashboards] = useState<PowerBiDashboards>([]);
		const [tiles, setTiles] = useState<PowerBiTiles>([]);
		const [tileModels, setTileModels] = useState<TileModel[]>([]);
		const [formData, setFormData] = useState<PowerBiTileFormData>({
			...initialGenericState(initialModel),
			powerBiGroupId: initialModel.powerBiGroupId,
			powerBiTileId: initialModel.powerBiTileId,
			dashboardId: initialModel.dashboardId,
		});
		const { showError } = useAlerts();

		const { powerBiGroupId, dashboardId } = formData;

		const formHandler: IFormHandler<PowerBiTileFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const powerBiTile: TileModel | undefined = useMemo(
			() => {
				const { powerBiTileId } = formData;
				return tileModels.find((tile) => tile.id === powerBiTileId);
			},
			[formData, tileModels]);

		const fetchGroups = useCallback(
			async (): Promise<PowerBiGroups> => {
				try {
					const data = await getGroups();

					const newGroups: PowerBiGroups = data.map((group) => ({
						text: group.title,
						value: group.id,
					}));

					const me: SelectOption<Guid | null> = {
						value: null,
						text: localize("_Me")
					};

					return [me, ...newGroups];
				} catch (e) {
					console.error(e);

					showError(localize("_CouldNotRetrieveTheGroupsFromPowerBI"));

					return [];
				}
			},
			[showError]);

		const fetchDashboard = useCallback(
			async (groupId: Guid | null): Promise<PowerBiDashboards> => {
				try {
					const data = await getDashboards(groupId);

					if (data.length === 0) {
						throw new Error("no dashboards for selected workspace");
					}

					const newDashboards: PowerBiDashboards = data.map(
						(board) => ({
							text: board.title,
							value: board.id,
						})
					);

					return newDashboards;
				} catch (e) {
					console.error(e);

					showError(localize("_CouldNotRetrieveTheDashboardsFromPowerBI"));

					return [];
				}
			},
			[showError]);

		const fetchTiles = useCallback(
			async (dashboardId: Guid): Promise<{ newTiles: PowerBiTiles; data: TileModel[]; }> => {
				try {
					const data: TileModel[] = await getTiles(dashboardId);

					const newTiles: PowerBiTiles = data.map((tile) => ({
						text: tile.title,
						value: tile.id,
					}));

					return { newTiles, data };
				} catch (e) {
					console.error(e);

					showError(localize("_CouldNotRetrieveTheTilesFromPowerBI"));

					return {
						newTiles: [],
						data: []
					};
				}
			},
			[showError]);

		const handleTileChange = useCallback((value: Guid | null): void => {
			setFormData(prevState => ({
				...prevState,
				powerBiTileId: value
			}));

			formHandler.for("powerBiTileId").validation.triggerValueChanged();
		}, [formHandler]);

		const handleDashboardChange = useCallback(async (value: Guid | null): Promise<void> => {
			if (value === dashboardId) {
				return;
			}

			setFormData(prevState => ({
				...prevState,
				dashboardId: value
			}));

			if (value) {
				const { newTiles, data } = await fetchTiles(value);
				const [firstTile] = newTiles;

				setTiles(newTiles);
				setTileModels(data);

				handleTileChange(firstTile?.value);
			} else {
				setTiles([]);
				handleTileChange(null);
			}

			formHandler.for("dashboardId").validation.triggerValueChanged();
		}, [dashboardId, fetchTiles, formHandler, handleTileChange]);

		const handleGroupChange = useCallback(async (value: Guid | null): Promise<void> => {
			if (value === powerBiGroupId) {
				return;
			}

			setFormData(prevState => ({
				...prevState,
				powerBiGroupId: value
			}));

			const dashboards: PowerBiDashboards = await fetchDashboard(value);
			const [firstDashboard] = dashboards;

			setDashboards(dashboards);

			handleDashboardChange(firstDashboard?.value);
		}, [fetchDashboard, handleDashboardChange, powerBiGroupId]);

		const init = async () => {
			if (mode.kind === "create") {
				const groups: PowerBiGroups = await fetchGroups();
				const [firstGroup] = groups;
				const dashboards: PowerBiDashboards = await fetchDashboard(firstGroup?.value);
				const [firstDashboard] = dashboards;
				const { newTiles, data } = await fetchTiles(firstDashboard?.value);
				const [firstTile] = newTiles;

				setGroups(groups);
				setDashboards(dashboards);
				setTiles(newTiles);
				setTileModels(data);

				setFormData(prevState => ({
					...prevState,
					powerBiGroupId: firstGroup?.value,
					dashboardId: firstDashboard?.value,
					powerBiTileId: firstTile?.value
				}));
			}

			if (mode.kind === "edit") {
				const newGroups: PowerBiGroups = await fetchGroups();
				const newDashboards: PowerBiDashboards = await fetchDashboard(formData.powerBiGroupId);
				const [firstDashboard] = newDashboards;
				const { newTiles, data } = await fetchTiles(formData.dashboardId ?? firstDashboard?.value);

				setGroups(newGroups);
				setDashboards(newDashboards);
				setTiles(newTiles);
				setTileModels(data);
			}
		};

		useEffect(() => {
			init();
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const { createHandler } = useCreateTile<PowerBiCreateTileModel>(
			formHandler,
			createPowerBiTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave
		);

		const { updateHandler } = useUpdateTile<PowerBiUpdateTileModel>(
			formHandler,
			updatePowerBiTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave
		);

		useImperativeHandle(ref, () => ({ createHandler, updateHandler }));

		return (
			<Form
				onSubmit="NONE"
			>
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>
				<FormItem label={localize("_PowerBiWorkspace")}>
					<Select
						value={formData.powerBiGroupId}
						onChange={handleGroupChange}
						options={groups}
						menuTitle={localize("_PowerBiWorkspace")}
					/>
					<ValidationError
						{...formHandler
							.for("powerBiGroupId")
							.attachToValidationError()}
					/>
				</FormItem>
				<FormItem label={localize("_Dashboard")}>
					<Select
						value={formData.dashboardId}
						onChange={handleDashboardChange}
						options={dashboards}
						menuTitle={localize("_Dashboard")}
					/>
					<ValidationError
						{...formHandler
							.for("dashboardId")
							.attachToValidationError()}
					/>
				</FormItem>
				<FormItem label={localize("_Tile")}>
					<Select
						value={formData.powerBiTileId}
						onChange={handleTileChange}
						options={tiles}
						menuTitle={localize("_Tile")}
					/>
					<ValidationError
						{...formHandler
							.for("powerBiTileId")
							.attachToValidationError()}
					/>
				</FormItem>
				<FormItem label={localize("_Preview")}>
					<div className={styles.powerBiFrame}>
						{powerBiTile && (
							<PowerBiEmbed
								embedUrlBase={initialModel.embedUrlBase}
								dashboardId={powerBiTile.dashboardId}
								tileId={powerBiTile.id}
								accessToken={powerBiTile.tokenInfo.token}
								trackingId="power-bi-tile-iframe"
							/>
						)
					}
					</div>
				</FormItem>
			</Form>
		);
	}
);

EditPowerBiTile.displayName = "EditPowerBiTile";

export const powerBiAppIframePermissions: readonly IFramePermission[] = [
	IFramePermission.Downloads,
	IFramePermission.Forms,
	IFramePermission.Modals,
	IFramePermission.OrientationLock,
	IFramePermission.PointerLock,
	IFramePermission.Popups,
	IFramePermission.EscapingPopups,
	IFramePermission.Presentation,
	IFramePermission.SameOrigin,
	IFramePermission.Scripts
];